import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { appStorage, appSession } from '../lib/safeStorage';
import { toast } from 'sonner';
import usePackageRequests from '../hooks/usePackageRequests';
const API = process.env.REACT_APP_BACKEND_URL;
/**
 * PackageRequestsDock — the app-wide "Needs you" card for package-switch
 * requests. Polls the tenant's pending requests and pins a glowing card
 * (top-right, every page) until the admin accepts or dismisses each one.
 * Accept applies the client's chosen package to their proposal in one tap.
 */
export default function PackageRequestsDock() {
  const navigate = useNavigate();
  const { requests, refresh } = usePackageRequests();
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const authHeaders = useCallback(() => {
    const token = appStorage.getItem('token') || appSession.getItem('token');
    return token ? { Authorization: 'Bearer ' + token } : {};
  }, []);
  useEffect(() => { if (idx >= requests.length) setIdx(0); }, [requests.length, idx]);
  if (!requests.length) return null;
  const cur = requests[Math.min(idx, requests.length - 1)];
  if (!cur) return null;
  const fmt = (n) => (n == null || n === '' || isNaN(Number(n))) ? null : '$' + Number(n).toLocaleString();
  const reqPrice = (cur.requestedAmount != null && String(cur.requestedAmount).trim() !== '' && !isNaN(Number(cur.requestedAmount)))
    ? '$' + Number(cur.requestedAmount).toLocaleString()
    : (cur.requestedPriceLabel || '');
  const curLabel = cur.currentPackage
    ? (cur.currentPackage + (fmt(cur.currentAmount) ? ' · ' + fmt(cur.currentAmount) : ''))
    : 'Current proposal';
  const accept = async () => {
    if (busy || !API) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/leads/events/${encodeURIComponent(cur.eventId)}/package-request/accept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      if (r.ok) {
        const d = await r.json().catch(() => ({}));
        toast.success(`Updated — ${cur.clientName} now sees ${cur.requestedName}` + (d && d.amount ? ' · $' + Number(d.amount).toLocaleString() : ''));
        setIdx(0);
        refresh();
      } else {
        toast.error('Could not update the proposal');
      }
    } catch { toast.error('Could not update the proposal'); }
    finally { setBusy(false); }
  };
  const dismiss = async () => {
    if (busy || !API) return;
    setBusy(true);
    try {
      await fetch(`${API}/api/leads/events/${encodeURIComponent(cur.eventId)}/package-request/dismiss`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      setIdx(0);
      refresh();
    } catch { /* silent */ }
    finally { setBusy(false); }
  };
  const openProposal = () => navigate(`/event/${cur.eventId}?section=proposal`);
  const PRIMARY = { background: 'linear-gradient(180deg,#E7C96A,#C79A2B)', color: '#0B0F1A', fontWeight: 800, fontSize: 12.5, border: 'none', borderRadius: 10, padding: '10px 14px', cursor: busy ? 'default' : 'pointer', flex: 1, textAlign: 'center', opacity: busy ? 0.7 : 1 };
  const GHOST = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#9AA1AD', fontSize: 11.5, fontWeight: 600, borderRadius: 10, padding: '10px 12px', cursor: busy ? 'default' : 'pointer' };
  return (
    <div style={{ position: 'fixed', top: 110, right: 20, zIndex: 55, width: 360, maxWidth: 'calc(100vw - 32px)' }} data-testid="package-requests-dock">
      <style>{`@keyframes pkgReqGlow{0%,100%{box-shadow:0 0 0 3px rgba(212,175,55,.12),0 18px 44px rgba(0,0,0,.5)}50%{box-shadow:0 0 0 5px rgba(212,175,55,.3),0 18px 48px rgba(0,0,0,.55)}}`}</style>
      <div style={{ background: 'linear-gradient(180deg,#101a30,#0b1324)', border: '1.5px solid #D4AF37', borderRadius: 14, padding: '15px 16px', animation: 'pkgReqGlow 2.4s ease-in-out infinite' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: '#0B0F1A', background: 'linear-gradient(180deg,#E7C96A,#C79A2B)', borderRadius: 999, padding: '4px 9px' }}>⚡ Needs you</span>
          {requests.length > 1 ? (
            <button type="button" onClick={() => setIdx((i) => (i + 1) % requests.length)} style={{ fontSize: 10.5, color: '#9AA1AD', background: 'none', border: 'none', cursor: 'pointer' }} data-testid="package-requests-next">{(Math.min(idx, requests.length - 1) + 1)} of {requests.length} · next ›</button>
          ) : (<span style={{ fontSize: 10.5, color: '#9AA1AD' }}>1 request</span>)}
        </div>
        <p style={{ fontSize: 13.5, color: '#eef0f6', lineHeight: 1.5, margin: '0 0 8px' }}>
          <b style={{ color: '#fff' }}>{cur.clientName}</b> wants a different package on their {cur.eventType ? cur.eventType + ' ' : ''}proposal.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#9AA1AD', margin: '0 0 13px', flexWrap: 'wrap' }}>
          <span style={{ textDecoration: 'line-through', opacity: 0.75 }}>{curLabel}</span>
          <span style={{ color: '#E7C75E', fontWeight: 800 }}>→</span>
          <span style={{ color: '#E7C75E', fontWeight: 700 }}>{cur.requestedName}{reqPrice ? ' · ' + reqPrice : ''}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" onClick={accept} disabled={busy} style={PRIMARY} data-testid="package-requests-accept">{busy ? 'Working…' : 'Accept & update'}</button>
          <button type="button" onClick={openProposal} disabled={busy} style={GHOST} data-testid="package-requests-open">Open proposal</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 9 }}>
          <span style={{ fontSize: 10, color: '#6B7280' }}>Stays until you accept or dismiss</span>
          <button type="button" onClick={dismiss} disabled={busy} style={{ fontSize: 10.5, color: '#8d93a0', background: 'none', border: 'none', cursor: busy ? 'default' : 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }} data-testid="package-requests-dismiss">Dismiss</button>
        </div>
      </div>
    </div>
  );
}
